import React, { ReactNode, useContext } from 'react';
import { Button, Spinner } from 'reactstrap';
import { useMutation } from '@apollo/client';
import { useStripe } from '@stripe/react-stripe-js';
import { Trans, useTranslation } from 'react-i18next';
import { GraphQLError } from 'graphql';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useLocation } from 'react-router-dom';
import { CREATE_ORDER } from '../graphql/mutations/order';
import { CampaignContext } from '../context/CampaignWrapper';
import StripeWrapper from '../context/StripeWrapper';
import IStripeCheckout, { IStripeButton } from './interfaces/IStripeCheckout';
import { DeliveryRadius } from '../generated/graphql';

const PAYMENT_ERRORS = {
  ADDRESS_OUT_OF_RANGE: 'ADDRESS_OUT_OF_RANGE',
  NO_STRIPE: 'NO_STRIPE',
};

const stripeErrorMessage = (
  <Trans i18nKey="COMPONENTS.CHECKOUT_CONTAINER.STRIPE_BUTTON.NO_STRIPE">
    There is no bank account associated with this web shop. Ask the administrator to follow
    {' '}
    <a href="https://intercom.help/trooper/nl/articles/3781254-een-stripe-account-aanmaken">
      these steps
    </a>
    .
  </Trans>
);

const StripeButton = ({
  isDisabled, label, order, orderId,
}:IStripeButton) => {
  const { campaign: { slug, deliveryOptions } } = useContext(CampaignContext);
  const [mutation, { loading }] = useMutation(CREATE_ORDER);
  const [translate] = useTranslation();
  const stripe = useStripe();
  const { search } = useLocation();

  const { deliveryRadii } = deliveryOptions || {};

  const buttonLabel = loading ? <Spinner /> : label;
  const isButtonDisabled = isDisabled || loading;

  const showServerError = (message?: ReactNode) => {
    toast.error(message);
  };

  const proceedToPayment = () => {
    if (!stripe) return;

    if (slug === 'demo') {
      window.location.href = `/demo-payment/${search}`;
    }

    mutation({
      variables: {
        slug,
        order,
        orderId,
      },
    })
      .then(({ data: { createStripeSession } }) => {
        const { stripeSessionId: sessionId } = createStripeSession || {};

        if (sessionId) {
          stripe.redirectToCheckout({ sessionId }).then(({ error }) => {
            if (error) {
              // If `redirectToCheckout` fails due to a browser or network
              // error, display the localized error message to your customer
              // using `result.error.message`.

              showServerError(error.message);
              Sentry.captureException(error);
            }
          });
        }
      })
      .catch((e) => {
        const { graphQLErrors = [], message } = e;

        const isAddressError = graphQLErrors.some(({ extensions }: GraphQLError) => extensions
          && extensions.code === PAYMENT_ERRORS.ADDRESS_OUT_OF_RANGE);

        const isStripeError = graphQLErrors.some(({ extensions }: GraphQLError) => extensions
          && extensions.code === PAYMENT_ERRORS.NO_STRIPE);

        if (isAddressError && deliveryRadii?.[0]) {
          if (deliveryRadii?.length === 1) {
            const parsedRadius = (deliveryRadii[0]?.radius || 0) / 1000;
            showServerError(translate('COMPONENTS.CHECKOUT_CONTAINER.STRIPE_BUTTON.ADDRESS_OUT_OF_RANGE', {
              radius: parsedRadius,
              address: deliveryRadii[0]?.centralAddress?.description,
            }));
          } else {
            const radii = [...deliveryRadii || []];
            const last = radii.pop();
            const maxFrom = (d : Maybe<DeliveryRadius>) => {
              const { radius, centralAddress } = d || { radius: 0, centralAddress: undefined };
              return translate('COMPONENTS.CHECKOUT_CONTAINER.STRIPE_BUTTON.ADDRESS_OUT_OF_RANGE_MAX_FROM', {
                radius: (radius || 0) / 1000,
                address: centralAddress?.description,
              });
            };
            const error = `${translate('COMPONENTS.CHECKOUT_CONTAINER.STRIPE_BUTTON.ADDRESS_OUT_OF_RANGE_plural')}
             ${radii.map(maxFrom).join(', ')}
             ${translate('COMPONENTS.CHECKOUT_CONTAINER.STRIPE_BUTTON.ADDRESS_OUT_OF_RANGE_OR')}
             ${maxFrom(last)}`;
            showServerError(error);
          }
        } else if (isStripeError) {
          showServerError(stripeErrorMessage);
        } else if (message) {
          showServerError(message);
        }

        Sentry.captureException(e);
      });
  };

  return (
    <Button
      color="primary"
      className="w-100"
      onClick={proceedToPayment}
      disabled={isButtonDisabled}
    >
      {buttonLabel}
    </Button>
  );
};

const StripeCheckout = ({
  isButtonDisabled, buttonLabel, order, orderId,
}:IStripeCheckout) => (
  <StripeWrapper>
    <StripeButton
      order={order}
      orderId={orderId}
      isDisabled={isButtonDisabled}
      label={buttonLabel}
    />
  </StripeWrapper>
);

export default StripeCheckout;
